/**
 * Appointment Protection & Recovery System
 * 
 * No-show prevention (reminders), detection, recovery flows,
 * and reschedule tracking.
 */

import type { Opportunity } from "./opportunityTypes";
import type { FollowUpTask } from "./opportunityTypes";
import { differenceInHours, differenceInMinutes, parseISO, isFuture, isPast, addHours, addDays } from "date-fns";
import { formatSafe } from "@/lib/dateUtils";

function parseDateSafe(val?: string | null): Date | null {
  if (!val) return null;
  try {
    const p = parseISO(val);
    if (!isNaN(p.getTime())) return p;
  } catch {}
  const d = new Date(val as string);
  return isNaN(d.getTime()) ? null : d;
}

/* ═══════ REMINDER SYSTEM ═══════ */

export type ReminderType = "day_before" | "morning_of" | "one_hour" | "confirmation";
export type ReminderStatus = "pending" | "sent" | "confirmed" | "skipped";

export interface Reminder {
  id: string;
  type: ReminderType;
  label: string;
  scheduledAt: string;
  status: ReminderStatus;
  channel: "sms" | "call" | "email";
  message: string;
}

export interface ProtectionStatus {
  appointmentId: string;
  appointmentTime: string;
  customerName: string;
  reminders: Reminder[];
  confirmationReceived: boolean;
  noShowRisk: "low" | "medium" | "high" | "critical";
  riskFactors: string[];
  protectionScore: number; // 0-100
  isProtected: boolean;
}

function buildReminders(appt: Opportunity): Reminder[] {
  if (!appt.appointmentTime) return [];
  const t = parseDateSafe(appt.appointmentTime);
  if (!t) return [];
  const now = new Date();
  const name = appt.primaryName || "there";

  const reminders: { type: ReminderType; label: string; time: Date; channel: "sms" | "call" | "email"; message: string }[] = [
    {
      type: "day_before",
      label: "24h Confirmation",
      time: addHours(t, -24),
      channel: "sms",
      message: `Hi ${name}, this is Koredor confirming your appointment tomorrow at ${formatSafe(t, "h:mm a")}. Reply YES to confirm or call us to reschedule. We look forward to seeing you!`,
    },
    {
      type: "morning_of",
      label: "Day-of Reminder",
      time: new Date(t.getFullYear(), t.getMonth(), t.getDate(), 8, 0),
      channel: "sms",
      message: `Good morning ${name}! Just a reminder about your appointment today at ${formatSafe(t, "h:mm a")}. Our team is looking forward to meeting with you.`,
    },
    {
      type: "one_hour",
      label: "1-Hour Alert",
      time: addHours(t, -1),
      channel: "sms",
      message: `Hi ${name}, your Koredor appointment is in 1 hour at ${formatSafe(t, "h:mm a")}. Our rep will be arriving shortly. See you soon!`,
    },
    {
      type: "confirmation",
      label: "Confirmation Request",
      time: addDays(t, -2),
      channel: "sms",
      message: `Hi ${name}, you have an appointment with Koredor scheduled for ${formatSafe(t, "MMM d")} at ${formatSafe(t, "h:mm a")}. Reply YES to confirm, or let us know if you need to reschedule.`,
    },
  ];

  return reminders
    .sort((a, b) => a.time.getTime() - b.time.getTime())
    .map((r) => ({
      id: `rem-${appt.id}-${r.type}`,
      type: r.type,
      label: r.label,
      scheduledAt: r.time.toISOString(),
      status: isPast(r.time) ? "sent" as ReminderStatus : "pending" as ReminderStatus,
      channel: r.channel,
      message: r.message,
    }));
}

export function getProtectionStatus(
  appt: Opportunity,
  conversations: { linkedLeadId?: string; linkedQuoteId?: string }[],
  messages: { conversationId: string; direction: string; timestamp: string }[],
): ProtectionStatus {
  if (!appt.appointmentTime) {
    return {
      appointmentId: appt.id,
      appointmentTime: "",
      customerName: appt.primaryName,
      reminders: [],
      confirmationReceived: false,
      noShowRisk: "high",
      riskFactors: ["No appointment time set"],
      protectionScore: 0,
      isProtected: false,
    };
  }

  const t = parseDateSafe(appt.appointmentTime);
  if (!t) {
    return {
      appointmentId: appt.id,
      appointmentTime: appt.appointmentTime || "",
      customerName: appt.primaryName,
      reminders: [],
      confirmationReceived: false,
      noShowRisk: "high",
      riskFactors: ["Invalid appointment time"],
      protectionScore: 0,
      isProtected: false,
    };
  }
  const reminders = buildReminders(appt);
  const now = new Date();
  const hoursUntil = differenceInHours(t, now);

  // Check for customer engagement
  const convo = conversations.find((c) => c.linkedLeadId === appt.id || c.linkedQuoteId === appt.id);
  const convoMsgs = convo
    ? messages.filter((m) => (m as any).conversationId === (convo as any).id)
    : [];
  const recentInbound = convoMsgs.filter(
    (m) => m.direction === "inbound" && differenceInHours(now, parseISO(m.timestamp)) < 72
  );
  const confirmationReceived = recentInbound.length > 0;

  // Risk factors
  const riskFactors: string[] = [];
  if (!confirmationReceived) riskFactors.push("No confirmation received");
  if (recentInbound.length === 0 && convoMsgs.length > 0) riskFactors.push("No recent customer response");
  if (convoMsgs.length === 0) riskFactors.push("No conversation history");
  if (hoursUntil < 2 && !confirmationReceived) riskFactors.push("Appointment imminent without confirmation");
  if ((appt.totalEstimate || 0) < 5000) riskFactors.push("Low-value appointment");

  // Score
  let score = 100;
  if (!confirmationReceived) score -= 30;
  if (convoMsgs.length === 0) score -= 20;
  if (recentInbound.length === 0 && convoMsgs.length > 0) score -= 15;
  if (hoursUntil < 2 && !confirmationReceived) score -= 20;
  if ((appt.totalEstimate || 0) < 5000) score -= 10;
  score = Math.max(0, Math.min(100, score));

  const noShowRisk: ProtectionStatus["noShowRisk"] =
    score >= 70 ? "low" : score >= 50 ? "medium" : score >= 25 ? "high" : "critical";

  const sentReminders = reminders.filter((r) => r.status === "sent");
  const isProtected = sentReminders.length >= 2 || confirmationReceived;

  // Update confirmation reminder if customer replied
  if (confirmationReceived) {
    const confReminder = reminders.find((r) => r.type === "confirmation");
    if (confReminder) confReminder.status = "confirmed";
  }

  return {
    appointmentId: appt.id,
    appointmentTime: appt.appointmentTime,
    customerName: appt.primaryName,
    reminders,
    confirmationReceived,
    noShowRisk,
    riskFactors,
    protectionScore: score,
    isProtected,
  };
}

/* ═══════ NO-SHOW DETECTION ═══════ */

export interface NoShowDetection {
  isNoShow: boolean;
  detectedAt: string;
  hoursOverdue: number;
  recoveryInitiated: boolean;
}

export function detectNoShow(appt: Opportunity): NoShowDetection | null {
  if (!appt.appointmentTime) return null;
  const t = parseDateSafe(appt.appointmentTime);
  if (!t) return null;
  if (isFuture(t)) return null;

  const now = new Date();
  const hoursOverdue = differenceInHours(now, t);

  // Only flag as no-show if past appointment time, no outcome recorded, and > 30min overdue
  const hasOutcome = appt.stage === "Closed Won" || appt.stage === "Closed Lost" ||
    appt.stage === "Follow-Up Needed" || appt.stage === "Appointment Completed" ||
    (appt.disposition && appt.disposition.reason);

  if (hasOutcome) return null;
  if (hoursOverdue < 0.5) return null;

  return {
    isNoShow: true,
    detectedAt: now.toISOString(),
    hoursOverdue,
    recoveryInitiated: false,
  };
}

/* ═══════ RECOVERY FLOW ═══════ */

export interface RecoveryAction {
  id: string;
  type: "reschedule_message" | "follow_up_task" | "priority_boost" | "manager_alert";
  label: string;
  description: string;
  status: "pending" | "executed";
  priority: "immediate" | "within_1h" | "within_24h";
}

export function getRecoveryActions(appt: Opportunity): RecoveryAction[] {
  const name = appt.primaryName || "Customer";
  const value = appt.totalEstimate || 0;
  const isHighValue = value >= 15000;

  const actions: RecoveryAction[] = [
    {
      id: `rec-${appt.id}-msg`,
      type: "reschedule_message",
      label: "Send Reschedule Message",
      description: `Auto-send: "Hi ${name}, we missed you today. Let's reschedule — reply with a time that works or I'll call you."`,
      status: "pending",
      priority: "immediate",
    },
    {
      id: `rec-${appt.id}-fu`,
      type: "follow_up_task",
      label: "Create Recovery Follow-Up",
      description: `Assign high-priority follow-up to ${appt.assignedRep || "available rep"} — call within 2 hours`,
      status: "pending",
      priority: "immediate",
    },
    {
      id: `rec-${appt.id}-boost`,
      type: "priority_boost",
      label: "Boost Queue Priority",
      description: "Move to top of follow-up queue with 'Recovery' tag",
      status: "pending",
      priority: "within_1h",
    },
  ];

  if (isHighValue) {
    actions.push({
      id: `rec-${appt.id}-mgr`,
      type: "manager_alert",
      label: "Alert Manager",
      description: `High-value no-show ($${value.toLocaleString()}) — escalate to sales manager`,
      status: "pending",
      priority: "immediate",
    });
  }

  return actions;
}

/* ═══════ RESCHEDULE TRACKING ═══════ */

export interface RescheduleMetrics {
  totalNoShows: number;
  recovered: number;
  rescheduled: number;
  lost: number;
  rescheduleRate: number;
  recoveryRate: number;
  recoveredRevenue: number;
  lostRevenue: number;
  avgRecoveryTimeHrs: number;
}

export function calculateRescheduleMetrics(
  opportunities: Opportunity[],
  followUpTasks: FollowUpTask[],
): RescheduleMetrics {
  const noShows = opportunities.filter((o) =>
    o.disposition?.reason === "no_show" || o.disposition?.reason === "not_home"
  );

  const rescheduled = noShows.filter((o) =>
    o.activities.some((a) => a.description.toLowerCase().includes("reschedule"))
  );

  const recovered = noShows.filter((o) => o.stage === "Closed Won");
  const lost = noShows.filter((o) => o.stage === "Closed Lost" && !o.activities.some((a) => a.description.toLowerCase().includes("reschedule")));

  const recoveredRevenue = recovered.reduce((s, o) => s + (o.totalEstimate || 0), 0);
  const lostRevenue = lost.reduce((s, o) => s + (o.totalEstimate || 0), 0);

  // Estimate avg recovery time from activities
  const recoveryTimes: number[] = [];
  recovered.forEach((o) => {
    const noShowAct = o.activities.find((a) => a.description.toLowerCase().includes("no show") || a.description.toLowerCase().includes("no_show"));
    const wonAct = o.activities.find((a) => a.description.toLowerCase().includes("sold") || a.description.toLowerCase().includes("won"));
    if (noShowAct && wonAct) {
      const diff = differenceInHours(parseISO(wonAct.createdAt), parseISO(noShowAct.createdAt));
      if (diff > 0) recoveryTimes.push(diff);
    }
  });
  const avgRecoveryTimeHrs = recoveryTimes.length > 0
    ? Math.round(recoveryTimes.reduce((a, b) => a + b, 0) / recoveryTimes.length)
    : 0;

  const totalNoShows = noShows.length;
  return {
    totalNoShows,
    recovered: recovered.length,
    rescheduled: rescheduled.length,
    lost: lost.length,
    rescheduleRate: totalNoShows > 0 ? Math.round((rescheduled.length / totalNoShows) * 100) : 0,
    recoveryRate: totalNoShows > 0 ? Math.round((recovered.length / totalNoShows) * 100) : 0,
    recoveredRevenue,
    lostRevenue,
    avgRecoveryTimeHrs,
  };
}

/* ═══════ PROTECTION SCORE CONFIG ═══════ */

export const RISK_CONFIG: Record<ProtectionStatus["noShowRisk"], { label: string; color: string; bg: string }> = {
  low:      { label: "Low Risk",      color: "text-success",      bg: "bg-success/10 border-success/30" },
  medium:   { label: "Medium Risk",   color: "text-warning",        bg: "bg-warning/10 border-warning/30" },
  high:     { label: "High Risk",     color: "text-warning",       bg: "bg-warning/10 border-warning/30" },
  critical: { label: "Critical Risk", color: "text-destructive",      bg: "bg-destructive/10 border-destructive/20" },
};
